import { cn } from '@portals-platform/ui';
import type { ReactNode } from 'react';
import {
  type FieldValues,
  FormProvider,
  type SubmitHandler,
  type UseFormReturn,
} from 'react-hook-form';

/**
 * The generics mirror `UseFormReturn`'s own, including `TTransformedValues`.
 *
 * A resolver may transform on the way out — `z.coerce`, a `.transform()`, a Zod schema
 * whose input and output types differ — in which case `handleSubmit` hands you the output
 * type, not the input type. Declaring only `TValues` here rejected every such form.
 */
export interface FormProps<
  TValues extends FieldValues,
  TContext = unknown,
  TTransformed extends FieldValues = TValues,
> {
  form: UseFormReturn<TValues, TContext, TTransformed>;
  onSubmit: SubmitHandler<TTransformed>;
  children: ReactNode;
  className?: string;
  id?: string;
}

/**
 * `FormProvider` and the `<form>` element in one.
 *
 * Every caller previously wired both by hand, and forgetting the provider fails at runtime
 * deep inside a field rather than at the call site.
 */
export function Form<
  TValues extends FieldValues,
  TContext = unknown,
  TTransformed extends FieldValues = TValues,
>({ form, onSubmit, children, className, id }: FormProps<TValues, TContext, TTransformed>) {
  return (
    <FormProvider {...form}>
      <form
        id={id}
        // Zod already validates; the browser's own bubbles would fire first and contradict
        // the field-level messages.
        noValidate
        onSubmit={form.handleSubmit(onSubmit)}
        className={cn('flex flex-col gap-8', className)}
      >
        {children}
      </form>
    </FormProvider>
  );
}
